import type { AuditLogEntry, JSONValue } from '../../store/modules/merchant/types';

export type PortalUserAccessSubject = {
  email?: string;
  givenName?: string;
  familyName?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Pulls the portal user out of a merchant audit log's before/after snapshot.
 *
 * Portal-user-access events store that person in `newValue` (grant) or `previousValue`
 * (revoke). Other audit resources do not have a subject, so this returns null.
 */
export const getPortalUserAccessSubject = (
  auditLog: AuditLogEntry,
): PortalUserAccessSubject | null => {
  if (auditLog.resource !== 'portalUserAccess') {
    return null;
  }

  const raw: JSONValue | null =
    auditLog.action === 'delete' ? auditLog.previousValue : auditLog.newValue;

  if (!isRecord(raw)) {
    return null;
  }

  return {
    email: typeof raw.email === 'string' ? raw.email : undefined,
    givenName: typeof raw.givenName === 'string' ? raw.givenName : undefined,
    familyName: typeof raw.familyName === 'string' ? raw.familyName : undefined,
  };
};

/** Full name for a portal-user-access subject, or empty when none was recorded. */
export const getPortalUserAccessSubjectName = (subject: PortalUserAccessSubject | null): string =>
  [subject?.givenName, subject?.familyName].filter(Boolean).join(' ').trim();
